import styled from '@emotion/styled';
import { spacer, color, elevation } from '@tokens';
import { forwardRef } from 'react';
import {
  baseInputGhostContainer,
  baseInputGhost,
} from '@components/Inputs/styles';
import { Icon } from '@components/Icon/Icon';
import { SelectedOptions } from './SelectedOptions';
import type { SelectionContainerProps } from './types';

// replace with a half-size spacer if/when available;
const MIN_INPUT_WIDTH = '150px';

// invalid/disabled cases to handle
const StyledContainer = styled.div<{
  isInvalid: boolean;
  isDisabled: boolean;
  size: 's' | 'm';
}>`
  width: 100%;
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: ${spacer.x0};
  ${({ isInvalid, isDisabled }) =>
    baseInputGhostContainer({
      isInvalid,
      isDisabled,
      isReadonly: false,
    })};
  ${({ isDisabled }) =>
    isDisabled
      ? `background-color: ${color.ui_background}; ${elevation(
          1,
        )}, inset 0 0 0 1px ${color.ui_disabled};`
      : ''};
  &:focus-visible {
    outline: none;
  }
  height: auto;
  min-height: ${({ size }) => (size === 's' ? spacer.x5 : spacer.x7)};
  ${({ size }) =>
    size === 's'
      ? `
    min-height: ${spacer.x5};
    padding-top: ${spacer.x1};
    padding-bottom: ${spacer.x1};
  `
      : `
    min-height: ${spacer.x7};
    padding-top: ${spacer.x1pt5};
    padding-bottom: ${spacer.x1pt5};
  `};
  padding-right: ${({ isInvalid }) => (isInvalid ? spacer.x5 : spacer.x2)};
  padding-left: ${spacer.x2};
`;

const StyledInput = styled.input<{ isInvalid: boolean; inputSize: 's' | 'm' }>`
  padding: 0;
  ${({ isInvalid }) => baseInputGhost(isInvalid)};
  flex-grow: 1;
  min-width: ${MIN_INPUT_WIDTH};
  padding-left: ${spacer.x1};
  height: ${({ inputSize }) => (inputSize === 's' ? spacer.x3 : spacer.x4)};
`;

const InvalidIcon = styled(Icon)`
  position: absolute;
  right: ${spacer.x2};
  top: 0;
  bottom: 0;
  margin: auto;
`;

export const SelectionContainer = forwardRef<
  HTMLDivElement,
  SelectionContainerProps
>(
  (
    {
      className,
      filterValue,
      selectedOptions,
      onChange,
      onFilterChange,
      onFilterFocus,
      onBlur,
      placeholder,
      isDisabled = false,
      isInvalid,
      size = 'm',
      inputRef,
    }: SelectionContainerProps,
    ref,
  ): JSX.Element => (
    <StyledContainer
      data-testid="multiselect_selected_items_container"
      className={className}
      ref={ref}
      tabIndex={0}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      onBlur={onBlur}
      size={size}
    >
      <SelectedOptions
        selectedOptions={selectedOptions}
        onChange={onChange}
        isDisabled={isDisabled}
        size={size}
      />
      <StyledInput
        ref={inputRef}
        value={filterValue}
        onChange={onFilterChange}
        onFocus={onFilterFocus}
        placeholder={isDisabled ? '' : placeholder}
        isInvalid={isInvalid}
        disabled={isDisabled}
        inputSize={size}
      />
      {isInvalid && (
        <InvalidIcon icon="warning_alt" size="m" color="text_error" />
      )}
    </StyledContainer>
  ),
);

SelectionContainer.displayName = 'SelectedOptionsContainer';
